import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Col, Row } from 'react-flexbox-grid'

import { Form, Text, DateTimePicker } from 'components/forms'
import Button from 'components/Button'
import { Modal } from 'components/Modal'
import { DataTable } from 'components/DataTable'
import { DateCell, TimeCell } from 'components/DataTableTest'

import styles from './ShopTimeSlot.module.scss'


const OpeningHours = ({ ranges = [], onChange }) => {
	const [t] = useTranslation()
	const [selectedSlot, setSelectedSlot] = useState()

	const handleMutateRow = mutateVals => {
		const isDeleting = typeof mutateVals === 'string' || typeof mutateVals === 'number'
		if(isDeleting) {
			onChange([{ id: mutateVals }])
		} else {
			setSelectedSlot(mutateVals)
		}
	}

	const handleSubmit = (values) => {
		onChange({
			id: selectedSlot?.id,
			flag: 99,
			description: values.description,
			date: values.date?.split('T')[0],
			openingTime: {
				from: values.openingTime?.from,
				to: values.openingTime?.to
			}
		})
		setSelectedSlot()
	}

	const handleTimePickerChange = (flag) => (value) => {
		onChange({
			flag,
			openingTime: {
				from: value?.from,
				to: value?.to
			}
		})
	}

	return (
		<Row>
			<Col xs={5}>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.monday')}
					value={ranges.find(({ flag }) => flag === 1)?.openingTime || {}}
					onChange={handleTimePickerChange(1)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.tuesday')}
					value={ranges.find(({ flag }) => flag === 2)?.openingTime || {}}
					onChange={handleTimePickerChange(2)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.wednesday')}
					value={ranges.find(({ flag }) => flag === 3)?.openingTime || {}}
					onChange={handleTimePickerChange(3)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.thursday')}
					value={ranges.find(({ flag }) => flag === 4)?.openingTime || {}}
					onChange={handleTimePickerChange(4)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.friday')}
					value={ranges.find(({ flag }) => flag === 5)?.openingTime || {}}
					onChange={handleTimePickerChange(5)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.saturday')}
					value={ranges.find(({ flag }) => flag === 6)?.openingTime || {}}
					onChange={handleTimePickerChange(6)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
				<DateTimePicker
					interval
					noForm
					picker='time'
					addDateToTime={false}
					label={t('common.dates.sunday')}
					value={ranges.find(({ flag }) => flag === 7)?.openingTime || {}}
					onChange={handleTimePickerChange(7)}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
				/>
			</Col>
			<Col xs={7}>
				<DataTable
					label={t('options_section.strings.exceptions')}
					data={ranges.filter(({ flag }) => flag === 99)}
					columns={({ locales }) => [
						{
							accessor: 'id',
							show: false
						}, {
							Header: t('common.fields.code'),
							accessor: 'timeSlot',
							show: false
						}, {
							Header: t('common.strings.description'),
							accessor: 'description'
						}, {
							Header: t('common.fields.date'),
							accessor: 'date',
							Cell: DateCell({ locales })
						}, {
							Header: t('common.fields.from_time'),
							accessor: 'openingTime.from',
							Cell: TimeCell({ locales })
						}, {
							Header: t('common.fields.to_time'),
							accessor: 'openingTime.to',
							Cell: TimeCell({ locales })
						}
					]}
					onAddRecord={() => setSelectedSlot({})}
					mutateRow={{
						onRowMutate: handleMutateRow,
						editBtn: true
					}}
					sortable={false}
					resizable={false}
					height={450}
					collapseOnDataChange={false}
				/>
				{selectedSlot &&
					<ExceptionTimeForm
						formData={selectedSlot}
						onClose={() => setSelectedSlot(null)}
						handleSubmit={handleSubmit}
					/>
				}
			</Col>
		</Row>
	)
}

const PopperContainer = ({ children }) => {
	const container = document.body
	return createPortal(
		children,
		container
	)
}

const ExceptionTimeForm = ({ onClose, formData, handleSubmit }) => {
	const [t] = useTranslation()
	return(
		<Modal
			unmount={onClose}
			label={t('common.strings.edit_values')}
			width={440}
		>
			<Form
				initialValues={formData}
				onSubmit={handleSubmit}
				reinitByModVer={false}
			>
				<Text field='description' label={t('common.strings.description')} />
				<DateTimePicker
					field='date'
					label={t('common.fields.date')}
					validate={val => !val ? t('validators.required') : null}
					validateOnChange
					popperContainer={PopperContainer}
					popperClassName={styles.popper}
				/>
				<DateTimePicker
					field='openingTime'
					picker='time'
					interval
					label={{ from: t('common.fields.from_time'), to: t('common.fields.to_time') }}
					addDateToTime={false}
					validate={val => !val || !val.from || !val.to ? t('validators.required') : null}
					validateOnChange
					popperContainer={PopperContainer}
					popperClassName={styles.popper}
				/>
				<div className={styles['buttons-wrapper']}>
					<Button
						text={t('common.buttons.abort')}
						status='cancelation'
						onClick={() => onClose()}
					/>
					<Button
						text={formData.id ? t('common.buttons.apply') : t('common.buttons.create')}
						type='submit'
						status='primary'
					/>
				</div>
			</Form>
		</Modal>
	)
}

export default OpeningHours
